import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { GlassCard } from "@/components/GlassCard";
import { Bell, Check, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

const TYPE_LABEL = {
  submission_received: "Submitted for review",
  changes_requested: "Changes requested",
  approved: "Approved",
  published: "Published",
  review_due: "Review due",
  feedback: "Feedback",
  new_sop: "New SOP",
};

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    const { data } = await api.get("/notifications");
    setItems(data);
  };

  useEffect(() => {
    (async () => {
      try {
        await load();
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  const markOne = async (id) => {
    await api.post(`/notifications/${id}/read`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-white">Notifications</h1>
          <p className="text-[#9AA4B2] text-sm mt-1">Every submission, review and publication in one place.</p>
        </div>
        <Button onClick={markAll} variant="ghost" className="btn-ghost-glass rounded-full" data-testid="notif-mark-all">
          <CheckCheck size={14} className="mr-1.5" /> Mark all read
        </Button>
      </header>

      {loading ? (
        <GlassCard className="p-10 animate-pulse-glow h-32" />
      ) : items.length === 0 ? (
        <GlassCard className="p-16 text-center">
          <div className="mx-auto w-14 h-14 rounded-2xl glass flex items-center justify-center text-cyan-300 mb-4">
            <Bell size={22} />
          </div>
          <div className="text-white font-display text-lg">You&apos;re all caught up.</div>
        </GlassCard>
      ) : (
        <div className="space-y-2">
          {items.map((n) => (
            <GlassCard
              key={n.id}
              hoverable
              className={`p-4 flex items-start gap-3 cursor-pointer ${
                !n.read ? "border-l-2 border-l-cyan-400" : ""
              }`}
              onClick={() => {
                markOne(n.id);
                if (n.related_sop_id) navigate(`/sops/${n.related_sop_id}`);
              }}
              data-testid={`notif-item-${n.id}`}
            >
              <div className={`w-9 h-9 shrink-0 rounded-2xl flex items-center justify-center ${
                n.read ? "bg-white/[0.04] text-[#9AA4B2]" : "bg-gradient-to-br from-cyan-500/30 to-indigo-500/30 text-cyan-200"
              }`}>
                <Bell size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-widest text-cyan-300">{TYPE_LABEL[n.type] || n.type}</div>
                <div className={`text-sm ${n.read ? "text-[#9AA4B2]" : "text-white"}`}>{n.message}</div>
                <div className="text-[11px] text-[#5B6472] mt-0.5">{new Date(n.created_at).toLocaleString()}</div>
              </div>
              {!n.read && (
                <button
                  className="p-2 text-[#9AA4B2] hover:text-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    markOne(n.id);
                  }}
                  aria-label="Mark read"
                >
                  <Check size={14} />
                </button>
              )}
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  );
}
